import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Textarea } from '@/components/atoms/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import { CATEGORY_ICONS, getCategoryIcon } from '@/lib/constants/category-icons';
import type { CreateProductFormData, UpdateProductFormData } from '../types/product-form.types';

interface Category {
  id: number | string;
  name: string;
}

interface Shop {
  id: string;
  name: string;
}

interface ProductBasicInfoProps<T extends CreateProductFormData | UpdateProductFormData> {
  form: UseFormReturn<T>;
  categories?: Category[];
  shops?: Shop[];
  isLoadingCategories?: boolean;
  isLoadingShops?: boolean;
  showShopSelect?: boolean;
  headerAction?: React.ReactNode;
}

export function ProductBasicInfo<T extends CreateProductFormData | UpdateProductFormData>({
  form,
  categories,
  shops,
  isLoadingCategories,
  isLoadingShops,
  showShopSelect = false,
  headerAction,
}: ProductBasicInfoProps<T>) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{t('products.basicInfo')}</CardTitle>
            <CardDescription>{t('products.basicInfoDescription')}</CardDescription>
          </div>
          {headerAction}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showShopSelect && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name={'shopId' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.shop')} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('products.selectShop')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingShops ? (
                        <SelectItem value="loading" disabled>
                          {t('common.loading')}
                        </SelectItem>
                      ) : (
                        shops?.map((shop) => (
                          <SelectItem key={shop.id} value={shop.id}>
                            {shop.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'categoryId' as any}
              render={({ field }) => {
                const selectedCategory = categories?.find(c => String(c.id) === field.value);
                const SelectedIcon = selectedCategory ? CATEGORY_ICONS[selectedCategory.name] : null;

                return (
                  <FormItem>
                    <FormLabel>{t('products.category')} *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('products.selectCategory')}>
                            {selectedCategory && (
                              <span className="flex items-center gap-2">
                                {SelectedIcon && <SelectedIcon className="size-4" />}
                                {t(`categories.${selectedCategory.name}`, { defaultValue: selectedCategory.name })}
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoadingCategories ? (
                          <SelectItem value="loading" disabled>
                            {t('common.loading')}
                          </SelectItem>
                        ) : (
                          categories?.map((category) => {
                            const Icon = getCategoryIcon(category.name);
                            return (
                              <SelectItem key={category.id} value={String(category.id)}>
                                <span className="flex items-center gap-2">
                                  <Icon className="size-4" />
                                  {t(`categories.${category.name}`, { defaultValue: category.name })}
                                </span>
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
        )}

        {!showShopSelect && (
          <FormField
            control={form.control}
            name={'categoryId' as any}
            render={({ field }) => {
              const selectedCategory = categories?.find(c => String(c.id) === field.value);
              const SelectedIcon = selectedCategory ? CATEGORY_ICONS[selectedCategory.name] : null;

              return (
                <FormItem>
                  <FormLabel>{t('products.category')} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('products.selectCategory')}>
                          {selectedCategory && (
                            <span className="flex items-center gap-2">
                              {SelectedIcon && <SelectedIcon className="size-4" />}
                              {t(`categories.${selectedCategory.name}`, { defaultValue: selectedCategory.name })}
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingCategories ? (
                        <SelectItem value="loading" disabled>
                          {t('common.loading')}
                        </SelectItem>
                      ) : (
                        categories?.map((category) => {
                          const Icon = getCategoryIcon(category.name);
                          return (
                            <SelectItem key={category.id} value={String(category.id)}>
                              <span className="flex items-center gap-2">
                                <Icon className="size-4" />
                                {t(`categories.${category.name}`, { defaultValue: category.name })}
                              </span>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        )}

        <FormField
          control={form.control}
          name={'name' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('products.productName')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('products.productNamePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'description' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('products.description')} *</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('products.descriptionPlaceholder')}
                  rows={4}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
